import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import { Alert } from '@material-ui/lab'
import { toast } from 'react-toastify'
import Connect from 'app/redux/connect'
import { Connected } from 'app/interface/redux'
import { userService } from 'app/service/user'
import { useGetAuth0User, useGetMMUser } from 'hooks'
import { useFeatureFlagEnabled } from 'posthog-js/react'

interface ProviderConfig {
  label: string
  color: string
  bg: string
  initial: string
}

function getProviderConfig(provider: string): ProviderConfig {
  switch (provider) {
    case 'auth0':
      return { label: 'Email / Password', color: '#5B4FCF', bg: '#EDE9FF', initial: 'E' }
    case 'google-oauth2':
      return { label: 'Google', color: '#EA4335', bg: '#FEF0EE', initial: 'G' }
    case 'facebook':
      return { label: 'Facebook', color: '#1877F2', bg: '#EEF4FF', initial: 'f' }
    default:
      return {
        label: provider,
        color: '#666',
        bg: '#f5f5f5',
        initial: provider[0]?.toUpperCase() ?? '?',
      }
  }
}

function MyAccountComponent(props: Connected) {
  const userId = props.core.session.user!.id
  const { loginWithPopup, getIdTokenClaims } = useAuth0()

  const { user: mmUser, fetchingMMUser } = useGetMMUser(userId, true)
  const { data: auth0User, refetch: refetchAuth0User } = useGetAuth0User(
    userId,
    !fetchingMMUser && !!mmUser && !!mmUser.acceptedInvite
  )

  const [firstName, setFirstName] = useState(mmUser?.firstName ?? '')
  const [lastName, setLastName] = useState(mmUser?.lastName ?? '')
  const [notificationEmail, setNotificationEmail] = useState(mmUser?.notificationEmail ?? '')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (mmUser) {
      setFirstName(mmUser.firstName ?? '')
      setLastName(mmUser.lastName ?? '')
      setNotificationEmail(mmUser.notificationEmail ?? '')
    }
  }, [mmUser])

  const hasChanges =
    firstName !== (mmUser?.firstName ?? '') ||
    lastName !== (mmUser?.lastName ?? '') ||
    notificationEmail !== (mmUser?.notificationEmail ?? '')

  const handleSaveProfile = async () => {
    if (!mmUser) return
    setIsSaving(true)
    try {
      await userService.updateByID(mmUser.id, { firstName, lastName, notificationEmail })
      toast.success('Profile updated successfully.')
    } catch (e) {
      toast.error('Failed to update profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false)
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null)
  const [confirmUnlink, setConfirmUnlink] = useState<{
    provider: string
    userId: string
    label: string
  } | null>(null)

  const googleAuthEnabled = useFeatureFlagEnabled('google-auth')
  const identities = auth0User?.identities ?? []
  const hasGoogleLinked = identities.some(id => id.provider === 'google-oauth2')
  const canUnlink = identities.length > 1

  const handleLinkGoogle = async () => {
    if (!mmUser) return
    setIsLinkingGoogle(true)
    try {
      await loginWithPopup({
        connection: 'google-oauth2',
        scope: 'openid profile email offline_access',
        prompt: 'consent',
      } as any)

      // Pass the popup scope so auth0-spa-js v1 looks up the correct cache entry
      const claims = await getIdTokenClaims({ scope: 'openid profile email offline_access' } as any)
      const sub = claims?.sub ?? ''
      const parts = sub.split('|')
      const provider = parts[0]
      const secondaryUserId = provider === 'google-oauth2' ? parts.slice(1).join('|') : undefined

      await userService.linkGoogle(mmUser.id, secondaryUserId)
      toast.success('Google account linked successfully!')
      refetchAuth0User()
    } catch (e: any) {
      if (e?.error === 'popup_closed_by_user') return
      if (e?.response?.status === 409) {
        toast.error(
          'This Google account is already linked to another user. Please use a different Google account.'
        )
      } else {
        toast.error('Failed to link Google account. Please try again.')
      }
    } finally {
      setIsLinkingGoogle(false)
    }
  }

  const handleUnlink = async () => {
    if (!confirmUnlink || !mmUser) return
    const { provider, userId: secondaryUserId } = confirmUnlink
    setUnlinkingId(secondaryUserId)
    setConfirmUnlink(null)
    try {
      await userService.unlinkIdentity(mmUser.id, provider, secondaryUserId)
      toast.success('Account unlinked successfully.')
      refetchAuth0User()
    } catch (e) {
      toast.error('Failed to unlink account. Please try again.')
    } finally {
      setUnlinkingId(null)
    }
  }

  return (
    <div className="container-fluid" style={{ maxWidth: 720, margin: '0 auto' }}>
      <div className="row mb-4">
        <div className="col-12">
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            User Preferences
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your personal account settings.
          </Typography>
        </div>
      </div>

      <Paper variant="outlined" style={{ padding: '24px', marginBottom: 24 }}>
        <Typography variant="h6" style={{ marginBottom: 4 }}>
          Profile
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
          Update your name and notification email.
        </Typography>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            variant="outlined"
            size="small"
            style={{ flex: 1, minWidth: 160 }}
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            variant="outlined"
            size="small"
            style={{ flex: 1, minWidth: 160 }}
          />
        </div>

        <TextField
          label="Email"
          value={mmUser?.email ?? ''}
          variant="outlined"
          size="small"
          fullWidth
          disabled
          style={{ marginBottom: 16 }}
        />

        <TextField
          label="Notification Email"
          value={notificationEmail}
          onChange={e => setNotificationEmail(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          type="email"
          style={{ marginBottom: 20 }}
          helperText="Where you receive system notifications (can differ from your login email)."
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveProfile}
          disabled={!hasChanges || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Paper>

      <Paper variant="outlined" style={{ padding: '24px' }}>
        <Typography variant="h6" style={{ marginBottom: 4 }}>
          Linked Identities
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
          Accounts you can use to log in.
        </Typography>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {fetchingMMUser || (!auth0User && !!mmUser?.acceptedInvite) ? (
            <CircularProgress size={24} />
          ) : identities.length === 0 ? (
            <Typography color="textSecondary">No identity information available.</Typography>
          ) : (
            identities.map((identity, i) => {
              const identityUserId = identity.user_id ?? ''
              const identityEmail = identity.profileData?.email ?? ''
              const isPrimary = i === 0
              const cfg = getProviderConfig(identity.provider)

              return (
                <Paper
                  key={`${identity.provider}|${identityUserId}`}
                  variant="outlined"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    gap: 16,
                    borderLeft: `4px solid ${cfg.color}`,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: cfg.bg,
                      color: cfg.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {cfg.initial}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        {cfg.label}
                      </Typography>
                      {isPrimary && (
                        <Chip
                          label="primary"
                          size="small"
                          style={{
                            height: 18,
                            fontSize: 11,
                            background: cfg.bg,
                            color: cfg.color,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </div>
                    <Typography variant="body2" color="textSecondary" noWrap>
                      {identityEmail || identity.connection}
                    </Typography>
                  </div>

                  {!isPrimary && (
                    <Tooltip
                      title={canUnlink ? 'Unlink this account' : 'Cannot unlink the only identity'}
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canUnlink || unlinkingId === identityUserId}
                          onClick={() =>
                            setConfirmUnlink({
                              provider: identity.provider,
                              userId: identityUserId,
                              label: cfg.label,
                            })
                          }
                        >
                          {unlinkingId === identityUserId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <LinkOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Paper>
              )
            })
          )}
        </div>

        {googleAuthEnabled && !hasGoogleLinked && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleLinkGoogle}
            disabled={isLinkingGoogle || !mmUser}
            startIcon={isLinkingGoogle ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isLinkingGoogle ? 'Linking...' : 'Link My Google Account'}
          </Button>
        )}
      </Paper>

      <Dialog open={!!confirmUnlink} onClose={() => setConfirmUnlink(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink Account</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to unlink the <strong>{confirmUnlink?.label}</strong> account? You
            will no longer be able to log in with it.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnlink(null)}>Cancel</Button>
          <Button onClick={handleUnlink} color="secondary" variant="contained">
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export const MyAccount = Connect(MyAccountComponent)
